// ============ Filter Selectors ============
export const selectFilters = (state) => state.search.filters;
export const selectAppliedFilters = (state) => state.search.appliedFilters;
export const selectQuery = (state) => state.search.filters.query;
export const selectAssignee = (state) => state.search.filters.assignee;
export const selectAuthor = (state) => state.search.filters.author;
export const selectSelectedTags = (state) => state.search.filters.selectedTags;
export const selectTagMethodOr = (state) => state.search.filters.tagMethodOr;
export const selectBookmarkedOnly = (state) => state.search.filters.bookmarkedOnly;
export const selectAncestorId = (state) => state.search.filters.ancestorId;
export const selectStatusFilter = (state) => state.search.filters.status;
export const selectChannelIds = (state) => state.search.filters.channelIds || [];
export const selectMediaTypeFilter = (state) => state.search.filters.mediaType;
export const selectSentBy = (state) => state.search.filters.sentBy;
export const selectSentTo = (state) => state.search.filters.sentTo;
export const selectDateFrom = (state) => state.search.filters.dateFrom;
export const selectDateTo = (state) => state.search.filters.dateTo;

// ============ Results Selectors ============
export const selectResults = (state) => state.search.results;
export const selectFolders = (state) => state.search.results.folders;
export const selectLinks = (state) => state.search.results.links;
export const selectMessages = (state) => state.search.results.messages;
export const selectTasks = (state) => state.search.results.tasks;
export const selectAncestorInfo = (state) => state.search.ancestorInfo;

// ============ Reference Data Selectors ============
export const selectUsers = (state) => state.search.users;
export const selectTags = (state) => state.search.tags;
export const selectRecentTags = (state) => state.search.recentTags;

// ============ Loading Selectors ============
export const selectIsLoading = (state) => state.search.isLoading;
export const selectIsFetchingNewData = (state) => state.search.isFetchingNewData;

// ============ UI Selectors ============
export const selectViewMode = (state) => state.search.viewMode;
export const selectActiveTab = (state) => state.search.activeTab;
export const selectPageSize = (state) => state.search.pageSize;
export const selectPage = (state) => state.search.page;
export const selectFoldersPage = (state) => state.search.foldersPage;
export const selectLinksPage = (state) => state.search.linksPage;
export const selectMessagesPage = (state) => state.search.messagesPage;
export const selectIsInitialized = (state) => state.search.isInitialized;

// ============ Derived Selectors ============

/**
 * Check if a specific filter field has been edited but not applied
 */
export const selectIsFieldEdited = (state, fieldName) => {
    const filters = state.search.filters;
    const applied = state.search.appliedFilters;

    switch (fieldName) {
        case 'value':
        case 'query':
            return filters.query !== applied.query;
        case 'assignee':
            return filters.assignee !== applied.assignee;
        case 'author':
            return filters.author !== applied.author;
        case 'tags':
            return JSON.stringify([...filters.selectedTags].sort()) !==
                JSON.stringify([...applied.selectedTags].sort());
        case 'tagMethodOr':
            return filters.tagMethodOr !== applied.tagMethodOr;
        case 'bookmarkedOnly':
            return filters.bookmarkedOnly !== applied.bookmarkedOnly;
        case 'status':
            return JSON.stringify([...filters.status].sort()) !==
                JSON.stringify([...applied.status].sort());
        default:
            return false;
    }
};

/**
 * Check if any filters have been applied (search executed)
 * Use this for determining whether to show results
 */
export const selectHasAppliedFilters = (state) => {
    const f = state.search.appliedFilters;
    return !!(f.assignee || f.author || f.query || f.bookmarkedOnly ||
        f.ancestorId || f.status.length > 0 || f.selectedTags.length > 0 ||
        (f.channelIds?.length > 0) || f.mediaType || f.sentBy || f.sentTo || f.dateFrom || f.dateTo);
};

/**
 * Get display tags (recent tags mapped to full tag objects, or first 10 tags)
 */
export const selectDisplayTags = (state) => {
    const { tags, recentTags } = state.search;

    if (recentTags?.length > 0) {
        return recentTags
            .map(tagName => tags.find(t => t.name === tagName))
            .filter(Boolean);
    }

    return tags.slice(0, 10);
};

/**
 * Get channels grouped by their group_id for hierarchical display.
 * Returns personal channel at top, then groups sorted alphabetically,
 * with "Others" section at the end for ungrouped channels.
 *
 * Edge cases handled:
 * - Multiple personalized channels: picks first by lowest id
 * - Missing created_by: gracefully skips personal channel detection
 * - Empty groups: filtered out (not displayed)
 */
export const selectChannelsGrouped = (state) => {
    const channels = state.channel.allChannels || [];
    const currentUserId = state.auth.user?.user_id ?? null;

    // Find personal channel (user's own personalized channel)
    // Handle edge case: multiple personalized channels - pick first by lowest id
    // Handle edge case: missing created_by - skip if can't determine ownership
    let personalChannel = null;
    if (currentUserId) {
        const personalChannels = channels.filter((c) => {
            if (!c.is_personalized) return false;
            // Handle created_by as either an id or an object with id
            const creatorId = typeof c.created_by === 'object' ? c.created_by?.id : c.created_by;
            return creatorId && String(creatorId) === String(currentUserId);
        });
        // Pick first by lowest id if multiple exist
        if (personalChannels.length > 0) {
            if (personalChannels.length > 1) {
                console.warn(`[selectChannelsGrouped] Found ${personalChannels.length} personalized channels for user ${currentUserId}, expected 1. Using lowest id.`);
            }
            personalChannel = personalChannels.sort((a, b) => a.id - b.id)[0];
        }
    }

    // Exclude ALL personalized channels from groups list
    // Only the user's own personal channel should appear (at top), not others' personalized channels
    const rest = channels.filter((c) => !c.is_personalized);

    const groupsMap = new Map();

    // Use a safe virtual key that won't collide with real numeric group_ids
    const UNGROUPED_KEY = '__ungrouped__';

    for (const ch of rest) {
        const gid = ch.group_id ?? UNGROUPED_KEY;
        const gname = ch.group_name ?? 'Others';

        if (!groupsMap.has(gid)) {
            groupsMap.set(gid, { groupId: gid, groupName: gname, channels: [] });
        }
        groupsMap.get(gid).channels.push(ch);
    }

    // Sort channels within groups alphabetically, filter out empty groups
    const groups = Array.from(groupsMap.values())
        .filter((g) => g.channels.length > 0)  // Skip empty groups
        .map((g) => ({
            ...g,
            channels: [...g.channels].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        }));

    // Sort groups alphabetically, keep "Others" (ungrouped) last
    groups.sort((a, b) => {
        const aUngrouped = a.groupId === UNGROUPED_KEY;
        const bUngrouped = b.groupId === UNGROUPED_KEY;
        if (aUngrouped && !bUngrouped) return 1;
        if (!aUngrouped && bUngrouped) return -1;
        return (a.groupName || '').localeCompare(b.groupName || '');
    });

    return { personalChannel, groups };
};

